/**
 * Three steps: details → review → receipt. Nothing is sent until the customer
 * presses "Confirm withdrawal" on the review step (art. 11a's confirmation
 * function). A failed or unmatched order lookup never blocks the withdrawal;
 * the Worker stores it flagged for the merchant to check.
 */
if (!customElements.get('withdrawal-form')) {
  customElements.define(
    'withdrawal-form',
    class WithdrawalForm extends HTMLElement {
      connectedCallback() {
        this.strings = VljStorefront.strings(this);
        this.details = this.querySelector('[data-step="details"]');
        this.review = this.querySelector('[data-step="review"]');
        this.done = this.querySelector('[data-step="done"]');
        this.error = this.details.querySelector('[data-error]');
        this.continueButton = this.details.querySelector('[data-continue]');

        const handoff = VljStorefront.prefill.take();
        if (handoff) {
          if (handoff.order) this.details.elements.order.value = handoff.order;
          if (handoff.email) this.details.elements.email.value = handoff.email;
          const firstEmpty = ['name', 'email', 'order'].map((n) => this.details.elements[n]).find((el) => !el.value);
          firstEmpty?.focus();
        }

        this.details.addEventListener('submit', (event) => {
          event.preventDefault();
          this.lookup();
        });
      }

      t(key, values) {
        return VljStorefront.format(this.strings[key], values);
      }

      input() {
        const el = this.details.elements;
        return { name: el.name.value.trim(), email: el.email.value.trim(), order: el.order.value.trim() };
      }

      showStep(step) {
        for (const el of [this.details, this.review, this.done]) el.hidden = el !== step;
        const intro = this.querySelector('.withdrawal__intro');
        if (intro) intro.hidden = step === this.done;
        if (step !== this.details) step.focus({ preventScroll: true });
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      setBusy(button, busy, label) {
        button.disabled = busy;
        button.setAttribute('aria-busy', String(busy));
        button.textContent = label;
      }

      async lookup() {
        const { name, email, order } = this.input();
        if (!name || !order || !email || !this.details.elements.email.checkValidity()) {
          this.error.textContent = this.t('invalid');
          this.error.hidden = false;
          return;
        }
        this.error.hidden = true;

        this.setBusy(this.continueButton, true, this.t('looking_up'));
        const { ok, status, data } = await VljStorefront.post('/track', { order, email });
        this.setBusy(this.continueButton, false, this.t('continue'));

        if (status === 429) {
          this.error.textContent = this.t('rate_limited');
          this.error.hidden = false;
          return;
        }
        if (status === 400) {
          this.error.textContent = this.t('invalid');
          this.error.hidden = false;
          return;
        }

        // Not found, or the lookup is down: continue unmatched.
        this.order = ok ? data : null;
        this.renderReview();
      }

      renderReview() {
        const e = VljStorefront.escape;
        const { name, email, order } = this.input();
        const found = this.order;
        const withdrawal = found?.withdrawal;

        const blocked = found && (withdrawal.reason === 'cancelled' || withdrawal.reason === 'exempt');
        const notices = [];
        if (!found) notices.push(['warn', this.t('unmatched')]);
        if (withdrawal?.reason === 'cancelled') notices.push(['', this.t('cancelled')]);
        if (withdrawal?.reason === 'exempt') notices.push(['', this.t('all_exempt')]);
        if (withdrawal?.reason === 'expired' && withdrawal.deadline) {
          notices.push(['warn', this.t('expired_note', { date: VljStorefront.formatDate(withdrawal.deadline) })]);
        }
        if (withdrawal?.existing) notices.push(['', this.t('existing', { reference: withdrawal.existing.reference })]);

        const items = found
          ? `<fieldset class="withdrawal__items">
              <legend>${e(this.t('items_legend'))}</legend>
              ${found.items
                .map(
                  (item) => `<label class="withdrawal__item${item.exempt ? ' withdrawal__item--disabled' : ''}">
                    <input type="checkbox" name="line" value="${e(item.id)}" ${item.exempt ? 'disabled' : 'checked'}
                      data-title="${e(item.variantTitle ? `${item.title} (${item.variantTitle})` : item.title)}" data-quantity="${e(item.quantity)}">
                    <span>
                      ${e(item.title)}
                      <span class="withdrawal__meta">${[item.variantTitle, `× ${item.quantity}`, item.exempt ? this.t('exempt_note') : null]
                        .filter(Boolean)
                        .map(e)
                        .join(' · ')}</span>
                    </span>
                  </label>`
                )
                .join('')}
            </fieldset>`
          : '';

        this.review.innerHTML = `
          <div class="withdrawal__card">
            <div>
              <h2 class="h3" style="margin:0">${e(this.t('review_heading'))}</h2>
              ${blocked ? '' : `<p class="withdrawal__meta">${e(this.t('review_intro'))}</p>`}
            </div>
            ${notices.map(([tone, text]) => `<p class="withdrawal__notice${tone ? ` withdrawal__notice--${tone}` : ''}">${e(text)}</p>`).join('')}
            <dl class="withdrawal__facts">
              <div><dt>${e(this.t('name_label'))}</dt><dd>${e(name)}</dd></div>
              <div><dt>${e(this.t('email_label'))}</dt><dd>${e(email)}</dd></div>
              <div><dt>${e(this.t('order_label'))}</dt><dd>${e(found ? found.order.name : order)}</dd></div>
            </dl>
            ${blocked ? '' : items}
            ${blocked ? '' : this.querySelector('[data-reason-template]').innerHTML}
            ${blocked ? '' : `<p class="withdrawal__statement" data-statement></p>`}
            <p class="withdrawal__error form__message" role="alert" data-review-error hidden></p>
            <div class="withdrawal__actions">
              ${blocked ? '' : `<button type="button" class="button" data-confirm>${e(this.t('confirm'))}</button>`}
              <button type="button" class="order-services__text-button" data-edit>${e(this.t('edit'))}</button>
            </div>
          </div>`;

        this.review.querySelector('[data-edit]').addEventListener('click', () => this.showStep(this.details));
        const confirm = this.review.querySelector('[data-confirm]');
        if (confirm) {
          confirm.addEventListener('click', () => this.submit(confirm));
          this.review.addEventListener('change', () => this.updateStatement());
          this.updateStatement();
        }
        this.showStep(this.review);
      }

      selectedLines() {
        return [...this.review.querySelectorAll('input[name="line"]:checked')];
      }

      /** Preview only; the Worker builds the authoritative statement and adds the timestamp. */
      updateStatement() {
        const { name, email, order } = this.input();
        const lines = this.selectedLines();
        const goods = this.order
          ? lines.map((line) => `${line.dataset.quantity} × ${line.dataset.title}`).join('; ')
          : this.t('whole_order').toLowerCase();
        this.review.querySelector('[data-statement]').textContent = [
          `I hereby give notice that I withdraw from my contract of sale of the following goods: ${goods || '—'}.`,
          `Order: ${this.order ? this.order.order.name : order}.`,
          `Name: ${name}.`,
          `Email: ${email}.`,
        ].join('\n');
      }

      async submit(button) {
        const error = this.review.querySelector('[data-review-error]');
        const lines = this.selectedLines();
        if (this.order && !lines.length) {
          error.textContent = this.t('no_items_selected');
          error.hidden = false;
          return;
        }
        error.hidden = true;

        const { name, email, order } = this.input();
        this.setBusy(button, true, this.t('sending'));
        const { ok, status, data } = await VljStorefront.post('/withdraw', {
          name,
          email,
          order,
          line_ids: lines.map((line) => line.value),
          reason: this.review.querySelector('select[name="reason"]')?.value || '',
        });

        if (!ok) {
          this.setBusy(button, false, this.t('confirm'));
          error.textContent =
            status === 429 ? this.t('rate_limited') : data.error === 'cancelled' ? this.t('cancelled') : data.error === 'exempt' ? this.t('all_exempt') : this.t('error');
          error.hidden = false;
          return;
        }

        this.renderDone(data, email);
      }

      renderDone(data, email) {
        const e = VljStorefront.escape;
        this.done.innerHTML = `
          <div class="withdrawal__card withdrawal__received">
            <span class="withdrawal__success-icon" aria-hidden="true">✓</span>
            <h2 class="h3">${e(this.t('received_heading'))}</h2>
            <p class="withdrawal__notice ${data.receipt === 'sent' ? 'withdrawal__notice--good' : 'withdrawal__notice--warn'}">
              ${e(data.receipt === 'sent' ? this.t('emailed', { email }) : this.t('email_delayed'))}
            </p>
            <dl class="withdrawal__facts">
              <div><dt>${e(this.t('reference'))}</dt><dd><strong>${e(data.reference)}</strong></dd></div>
              <div><dt>${e(this.t('submitted_at'))}</dt><dd>${e(data.submittedAtDisplay)}</dd></div>
            </dl>
            <div>
              <h3 class="withdrawal__subheading" style="margin-bottom:0.8rem">${e(this.t('statement'))}</h3>
              <p class="withdrawal__statement">${e(data.statement)}</p>
            </div>
            ${this.querySelector('[data-returns-template]').innerHTML}
            <div class="withdrawal__actions">
              <button type="button" class="button button--secondary" data-print>${e(this.t('print'))}</button>
            </div>
          </div>`;
        this.done.querySelector('[data-print]').addEventListener('click', () => window.print());
        this.showStep(this.done);
      }
    }
  );
}
